
import { useState, useEffect, useCallback } from 'react';
import { UserTechnique } from '@/types/technique';
import { supabase } from '@/integrations/supabase/client';
import { getVideosForTechnique, YouTubeVideo } from '@/services/youtubeVideoService';

// Use the same VideoSortOption type as YouTubeVideosList
export type VideoSortOption = 'newest' | 'oldest' | 'alphabetical';

// Support both use cases: checking if techniques have videos, and fetching videos for a specific technique
export const useTechniqueVideos = (
  techniqueIdOrTechniques: string | UserTechnique[]
) => {
  const [techniquesWithVideos, setTechniquesWithVideos] = useState<Record<string, boolean>>({});
  const [videos, setVideos] = useState<YouTubeVideo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sortBy, setSortBy] = useState<VideoSortOption>('newest');

  // Fetch technique videos based on whether we received an array or a single ID
  useEffect(() => {
    const fetchTechniqueVideos = async () => {
      setIsLoading(true);
      try {
        // Case 1: We received an array of UserTechnique objects
        if (Array.isArray(techniqueIdOrTechniques)) {
          const techniques = techniqueIdOrTechniques;
          if (techniques.length === 0) {
            setIsLoading(false);
            return;
          }
          
          const techniqueIds = techniques.map(t => t.technique?.id).filter(Boolean) as string[];
          
          if (techniqueIds.length === 0) {
            setIsLoading(false);
            return;
          }
          
          const { data, error } = await supabase
            .from('youtube_videos')
            .select('technique_id')
            .in('technique_id', techniqueIds);
          
          if (error) throw error;
          
          // Create a map of technique_id -> has_videos
          const videosMap: Record<string, boolean> = {};
          data.forEach((item: { technique_id: string }) => {
            videosMap[item.technique_id] = true;
          });
          
          setTechniquesWithVideos(videosMap);
        } 
        // Case 2: We received a single technique ID
        else if (typeof techniqueIdOrTechniques === 'string') {
          const techniqueId = techniqueIdOrTechniques;
          const fetchedVideos = await getVideosForTechnique(techniqueId);
          setVideos(fetchedVideos);
        }
      } catch (error) {
        console.error('Error fetching technique videos:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchTechniqueVideos();
  }, [techniqueIdOrTechniques]);

  // Sort videos based on the current sort option
  const sortedVideos = useCallback(() => {
    if (!videos) return [];
    
    return [...videos].sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      } else if (sortBy === 'oldest') {
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      } else if (sortBy === 'alphabetical') {
        return (a.title || '').localeCompare(b.title || '');
      }
      return 0;
    });
  }, [videos, sortBy]);

  // Handler for when videos are added or deleted
  const handleVideoAdded = useCallback(async () => {
    if (typeof techniqueIdOrTechniques !== 'string') return;
    
    try {
      setIsLoading(true);
      const updatedVideos = await getVideosForTechnique(techniqueIdOrTechniques);
      setVideos(updatedVideos);
    } catch (error) {
      console.error('Error refreshing videos:', error);
    } finally {
      setIsLoading(false);
    }
  }, [techniqueIdOrTechniques]);

  // Handler for changing the sort option
  const handleSortChange = useCallback((option: VideoSortOption) => {
    setSortBy(option);
  }, []);

  // Return different values based on the input type
  if (Array.isArray(techniqueIdOrTechniques)) {
    return { techniquesWithVideos, isLoading };
  } else {
    return {
      videos: sortedVideos(),
      handleVideoAdded,
      sortBy,
      handleSortChange,
      isLoading
    };
  }
};
